// Voice note — the alternative to typing for an agent who may be moving, or shaking. Hold to
// talk, release to stop; a quick tap locks recording on so the phone can go in a pocket, and a
// second tap stops it. Auto-stops at two minutes. No sound, no vibration, just a small dot.
import './voice-note.scss';

import { type FC, type PointerEvent, useEffect, useRef, useState } from 'react';

import { formatClock, type IVoiceNote, MAX_VOICE_SEC, pickAudioMimeType, validateVoiceNote } from './validate';

interface IVoiceNoteRecorderProps {
	value: IVoiceNote | null;
	onChange: (note: IVoiceNote | null) => void;
	disabled?: boolean;
}

type Phase = 'idle' | 'starting' | 'holding' | 'locked' | 'review';

/** Released sooner than this after pressing = a tap = lock recording on. */
const TAP_MS = 350;

const MicGlyph = () => (
	<svg
		width='22'
		height='22'
		viewBox='0 0 24 24'
		fill='none'
		stroke='currentColor'
		strokeWidth='1.8'
		strokeLinecap='round'
		strokeLinejoin='round'
		aria-hidden='true'
	>
		<rect
			x='9'
			y='3'
			width='6'
			height='11'
			rx='3'
		/>
		<path d='M5 11a7 7 0 0 0 14 0M12 18v3' />
	</svg>
);

export const VoiceNoteRecorder: FC<IVoiceNoteRecorderProps> = ({ value, onChange, disabled }) => {
	const [phase, setPhase] = useState<Phase>(value ? 'review' : 'idle');
	const [elapsed, setElapsed] = useState(0);
	const [error, setError] = useState('');
	const [url, setUrl] = useState<string | null>(null);

	const streamRef = useRef<MediaStream | null>(null);
	const recorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const startedAtRef = useRef(0);
	const pressedAtRef = useRef(0);
	const releasedEarlyRef = useRef(false);
	const tickRef = useRef<number | null>(null);

	const supported =
		typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

	useEffect(() => {
		if (!value) {
			setUrl(null);
			return;
		}
		const next = URL.createObjectURL(value.blob);
		setUrl(next);
		return () => URL.revokeObjectURL(next);
	}, [value]);

	const releaseStream = () => {
		streamRef.current?.getTracks().forEach((t) => t.stop());
		streamRef.current = null;
		if (tickRef.current) window.clearInterval(tickRef.current);
		tickRef.current = null;
	};

	useEffect(() => () => releaseStream(), []);

	const stop = () => {
		const rec = recorderRef.current;
		if (rec && rec.state !== 'inactive') rec.stop();
	};

	const start = async () => {
		setError('');
		setPhase('starting');
		pressedAtRef.current = Date.now();
		releasedEarlyRef.current = false;
		let stream: MediaStream;
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		} catch (e) {
			const denied = (e as DOMException)?.name === 'NotAllowedError';
			setError(
				denied ? 'Microphone blocked. Allow it in your browser settings, or type a note instead.' : 'No microphone found.'
			);
			setPhase('idle');
			return;
		}
		streamRef.current = stream;
		const mimeType = pickAudioMimeType();
		let rec: MediaRecorder;
		try {
			rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
		} catch {
			releaseStream();
			setError('This phone can’t record voice notes here. Type a note instead.');
			setPhase('idle');
			return;
		}
		chunksRef.current = [];
		rec.ondataavailable = (ev) => {
			if (ev.data.size) chunksRef.current.push(ev.data);
		};
		rec.onstop = () => {
			const durationSec = (Date.now() - startedAtRef.current) / 1000;
			const mime = rec.mimeType || mimeType || 'audio/webm';
			const blob = new Blob(chunksRef.current, { type: mime.split(';')[0] });
			releaseStream();
			const check = validateVoiceNote({ bytes: blob.size, durationSec, mime: blob.type });
			if (!check.ok) {
				setError(check.error);
				setPhase('idle');
				return;
			}
			onChange({ blob, durationSec: Math.round(durationSec), mime: blob.type });
			setPhase('review');
		};
		recorderRef.current = rec;
		startedAtRef.current = Date.now();
		setElapsed(0);
		rec.start(1000);
		tickRef.current = window.setInterval(() => {
			const s = (Date.now() - startedAtRef.current) / 1000;
			setElapsed(s);
			if (s >= MAX_VOICE_SEC) stop();
		}, 250);
		// Released while the permission prompt / stream was still opening → that was a tap.
		setPhase(releasedEarlyRef.current ? 'locked' : 'holding');
	};

	const onPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
		if (disabled || !supported) return;
		e.preventDefault();
		if (phase === 'locked') {
			stop();
			return;
		}
		if (phase === 'idle') void start();
	};

	const onPointerUp = () => {
		if (phase === 'starting') {
			releasedEarlyRef.current = true;
			return;
		}
		if (phase !== 'holding') return;
		if (Date.now() - pressedAtRef.current < TAP_MS) setPhase('locked');
		else stop();
	};

	const discard = () => {
		onChange(null);
		setPhase('idle');
		setElapsed(0);
	};

	if (!supported) {
		return <p className='voice-note__hint'>Voice notes aren’t available on this browser. Type a note instead.</p>;
	}

	if (phase === 'review' && value) {
		return (
			<div className='voice-note voice-note--review'>
				{url ? (
					// The agent's own just-recorded voice — there is no caption track to offer.
					// eslint-disable-next-line jsx-a11y/media-has-caption
					<audio
						className='voice-note__audio'
						src={url}
						controls
						preload='metadata'
					/>
				) : null}
				<span className='voice-note__meta'>{formatClock(value.durationSec)}</span>
				<button
					type='button'
					className='voice-note__discard'
					onClick={discard}
					disabled={disabled}
				>
					Delete
				</button>
			</div>
		);
	}

	const recording = phase === 'holding' || phase === 'locked';
	const label =
		phase === 'locked'
			? 'Tap to stop'
			: phase === 'holding'
				? 'Release to stop · tap to lock'
				: phase === 'starting'
					? 'Opening microphone…'
					: 'Hold to talk';

	return (
		<div className='voice-note'>
			<button
				type='button'
				className={`voice-note__btn${recording ? ' is-recording' : ''}`}
				onPointerDown={onPointerDown}
				onPointerUp={onPointerUp}
				onPointerLeave={phase === 'holding' ? onPointerUp : undefined}
				onContextMenu={(e) => e.preventDefault()}
				onKeyDown={(e) => {
					// Keyboard: Enter/Space toggle a locked recording.
					if (e.key !== 'Enter' && e.key !== ' ') return;
					e.preventDefault();
					if (phase === 'idle') {
						releasedEarlyRef.current = true;
						void start();
					} else if (recording) stop();
				}}
				disabled={disabled}
				aria-pressed={recording}
				aria-label={recording ? `Recording ${formatClock(elapsed)}. ${label}` : 'Record a voice note'}
			>
				<MicGlyph />
				<span className='voice-note__label'>{label}</span>
				{recording ? (
					<span className='voice-note__timer'>
						<span
							className='voice-note__dot'
							aria-hidden='true'
						/>
						{formatClock(elapsed)} / {formatClock(MAX_VOICE_SEC)}
					</span>
				) : null}
			</button>
			{error ? (
				<p
					className='voice-note__error'
					role='alert'
				>
					{error}
				</p>
			) : null}
		</div>
	);
};

export default VoiceNoteRecorder;
